import { Temporal } from '@js-temporal/polyfill';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent } from 'react';

import {
  accessQuery,
  type AccessMode,
  type DeliveryHours,
  roomsQuery,
  setAccess,
  updateRoom,
  type Venue,
  type VenueAccessInput,
  venueFilesQuery,
  venueSettingsQuery,
  WEEKDAYS,
} from '../api/venues.ts';
import { venueJobsQuery, type VenueJob } from '../api/venue-page.ts';
import { bookDock, docksToBookQuery } from '../api/venue-warnings.ts';
import { Button } from '../components/button.tsx';
import { FactTile } from '../components/facts.tsx';
import { Popover } from '../components/popover.tsx';
import { dayName, zoneName } from './roster-bars.tsx';
import { OpenButton } from './venue-files.tsx';
import { icons } from '../components/icons.ts';
import { Field } from '../components/field.tsx';

// CB-REQ VEN-02 at v1.973 (venue slice 1, part B): how to get in and what fits. Standard fields where
// AVenue can check something, notes for the rest; the company chooses which the form asks for, and
// fields it hides keep what they held, because saving replaces the venue's access whole. Hours and
// the curfew are local times at the venue, in its own zone.

const DAYS = [1, 2, 3, 4, 5, 6, 7];

/**
 * VEN-01 at v1.9104, the operator's Access board of 2026-10-26 (V-5): the access facts as tiles,
 * delivery hours as a bar per weekday in the venue's zone, the dock bookings of the coming jobs with
 * *Book* on the unbooked, the notes, and the dock photos. *Edit access* sits on the tab bar and
 * opens the form, unchanged, in a popover. A company that records notes only sees no tiles.
 */
export function AccessSection({
  venue,
  mayManage,
  onChanged,
  editing = false,
  onEditDone = () => {},
  capabilities = [],
  tenantZone = 'Australia/Melbourne',
}: {
  venue: Venue;
  mayManage: boolean;
  onChanged: () => Promise<void>;
  editing?: boolean;
  onEditDone?: () => void;
  capabilities?: readonly string[];
  tenantZone?: string;
}) {
  const access = useQuery(accessQuery(venue.id));
  const settings = useQuery(venueSettingsQuery);
  const mode: AccessMode = settings.data?.mode ?? 'fields_and_notes';
  const zone = venue.timeZone ?? tenantZone;
  const a = access.data ? complete(access.data) : null;

  return (
    <section aria-labelledby="access" className="flex flex-col gap-4">
      <h2 id="access" className="sr-only">
        Getting in
      </h2>
      {access.isError ? (
        <p role="alert" className="alert">
          {access.error.message}
        </p>
      ) : null}
      {a && editing ? (
        <Popover title="Edit access" onClose={onEditDone}>
          <AccessForm
            venueId={venue.id}
            access={a}
            mode={mode}
            zone={venue.timeZone ?? 'the company’s time zone'}
            onDone={onEditDone}
            onChanged={onChanged}
          />
        </Popover>
      ) : null}
      {/* Until someone records access, its yes-or-no fields are only defaults, not facts. */}
      {a && !venue.checklist.access ? (
        <p className="text-sm text-ink-soft">Nothing recorded about getting in yet.</p>
      ) : null}
      {a && venue.checklist.access && mode !== 'notes_only' ? <AccessTiles access={a} /> : null}
      {a ? (
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
          {mode !== 'notes_only' ? <DeliveryHoursPanel access={a} zone={zone} /> : null}
          {capabilities.includes('jobs.view') ? (
            <DockBookings
              venueId={venue.id}
              zone={zone}
              required={a.dockBookingRequired}
              mayBook={capabilities.includes('jobs.manage')}
            />
          ) : null}
          <section className="panel flex flex-col gap-2 p-4" aria-labelledby="access-notes">
            <h3 id="access-notes" className="panel-label">
              Notes
            </h3>
            <AccessNotes access={a} />
            <RoomNotes venueId={venue.id} mayManage={mayManage} />
          </section>
          <DockPhotos venueId={venue.id} />
        </div>
      ) : null}
    </section>
  );
}

const inMetres = (mm: number) =>
  `${(mm / 1000).toLocaleString('en-AU', { maximumFractionDigits: 2 })}`;

function AccessTiles({ access: a }: { access: VenueAccessInput }) {
  const lift =
    a.liftWidthMm && a.liftDepthMm && a.liftHeightMm
      ? `${inMetres(a.liftWidthMm)} × ${inMetres(a.liftDepthMm)} × ${inMetres(a.liftHeightMm)} m`
      : '—';
  return (
    <div className="fact-tiles fact-tiles-boxed">
      <FactTile
        icon={<icons.transport size={20} aria-hidden />}
        value={a.vehicleMaxLengthMm ? `${inMetres(a.vehicleMaxLengthMm)} m` : '—'}
        label="Longest vehicle"
      />
      <FactTile
        icon={<icons.lift size={20} aria-hidden />}
        value={lift}
        label={
          a.liftCapacityKg
            ? `Goods lift · ${a.liftCapacityKg.toLocaleString('en-AU')} kg`
            : 'Goods lift'
        }
      />
      <FactTile
        icon={<icons.doors size={20} aria-hidden />}
        value={
          a.liftDoorWidthMm && a.liftDoorHeightMm
            ? `${inMetres(a.liftDoorWidthMm)} × ${inMetres(a.liftDoorHeightMm)} m`
            : '—'
        }
        label="Lift door"
      />
      <FactTile
        icon={<icons.dock size={20} aria-hidden />}
        value={
          !a.dockBookingRequired
            ? 'Not needed'
            : a.dockBookingLeadDays === null
              ? 'Needed'
              : `${a.dockBookingLeadDays} ${a.dockBookingLeadDays === 1 ? 'day' : 'days'}`
        }
        label="Dock booking notice"
      />
      <FactTile
        icon={<icons.showEnd size={20} aria-hidden />}
        value={a.curfew ?? 'None'}
        label="Curfew"
      />
      <FactTile
        icon={<icons.depot size={20} aria-hidden />}
        value={a.bondStore ? 'Yes' : 'No'}
        label="Bond store"
      />
      <FactTile
        icon={<icons.accessHours size={20} aria-hidden />}
        value={a.access24h ? 'Yes' : 'No'}
        label="24-hour access"
      />
    </div>
  );
}

const minutes = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));

/** VEN-02: each weekday's delivery windows as bars across the day, in the venue's zone. */
function DeliveryHoursPanel({ access: a, zone }: { access: VenueAccessInput; zone: string }) {
  return (
    <section className="panel flex flex-col gap-2 p-4" aria-labelledby="delivery-hours">
      <div className="flex items-center">
        <h3 id="delivery-hours" className="panel-label flex-1">
          Delivery hours
        </h3>
        <span className="text-xs text-muted">{zoneName(zone)}</span>
      </div>
      {a.deliveryHours.length === 0 ? (
        <p className="text-sm text-muted">None recorded</p>
      ) : (
        <>
          <ul className="flex flex-col">
            {DAYS.map((d) => {
              const windows = a.deliveryHours.filter((h) => h.weekday === d);
              return (
                <li key={d} className="hours-row">
                  <span className="font-semibold">{WEEKDAYS[d]!.slice(0, 3)}</span>
                  <span className="hours-track" aria-hidden="true">
                    {windows.map((h, i) => {
                      const from = minutes(h.opens) / 1440;
                      const to = (minutes(h.closes) || 1440) / 1440;
                      return (
                        <span
                          key={i}
                          className="hours-window"
                          style={{ left: `${from * 100}%`, width: `${(to - from) * 100}%` }}
                        />
                      );
                    })}
                  </span>
                  <span className="text-right text-xs text-muted">
                    {windows.length === 0
                      ? 'Closed'
                      : windows.map((h) => `${h.opens} – ${h.closes}`).join(', ')}
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="hours-axis text-xs text-muted" aria-hidden="true">
            <span>00:00</span>
            <span>06:00</span>
            <span>12:00</span>
            <span>18:00</span>
            <span>24:00</span>
          </div>
        </>
      )}
    </section>
  );
}

/**
 * VEN-02: the coming fortnight's jobs here, in and out, each with its dock booking — one per job,
 * its reference — or *Book* where the venue asks for one and none is recorded.
 */
function DockBookings({
  venueId,
  zone,
  required,
  mayBook,
}: {
  venueId: string;
  zone: string;
  required: boolean;
  mayBook: boolean;
}) {
  // The read takes instants: from the start of today in the venue's zone, for 14 days.
  const start = Temporal.Now.zonedDateTimeISO(zone).startOfDay();
  const today = start.toPlainDate();
  const jobs = useQuery(
    venueJobsQuery(
      venueId,
      start.toInstant().toString(),
      start.add({ days: 14 }).toInstant().toString(),
    ),
  );
  const [booking, setBooking] = useState<VenueJob | null>(null);
  const now = Temporal.Now.instant();
  const rows = (jobs.data ?? [])
    .filter((j) => j.status !== 'cancelled' && !j.venueGearOnly)
    .flatMap((j) => [
      { job: j, way: 'in' as const, at: j.outAt ?? j.scheduledWindow.startsAt },
      { job: j, way: 'out' as const, at: j.backAt ?? j.scheduledWindow.endsAt },
    ])
    .filter((r) => Temporal.Instant.compare(Temporal.Instant.from(r.at), now) >= 0)
    .sort((x, y) =>
      Temporal.Instant.compare(Temporal.Instant.from(x.at), Temporal.Instant.from(y.at)),
    );
  const todayText = today.toString();
  return (
    <section className="panel flex flex-col gap-2 p-4" aria-labelledby="dock-bookings">
      {booking ? (
        <Popover title={`Dock booked · ${booking.reference}`} onClose={() => setBooking(null)}>
          <BookDock job={booking} onDone={() => setBooking(null)} />
        </Popover>
      ) : null}
      <h3 id="dock-bookings" className="panel-label">
        Dock bookings
      </h3>
      {rows.length === 0 ? (
        <p className="text-sm text-muted">No jobs in or out in the next 14 days.</p>
      ) : (
        <table className="data-table text-sm">
          <caption className="sr-only">Dock bookings, the next 14 days</caption>
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">Job</th>
              <th scope="col">Ref</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const local = Temporal.Instant.from(r.at).toZonedDateTimeISO(zone);
              const day = local.toPlainDate().toString();
              return (
                <tr key={`${r.job.id}-${r.way}`}>
                  <td>
                    {day === todayText ? 'Today' : dayName(day)}{' '}
                    {local.toPlainTime().toString({ smallestUnit: 'minute' })}
                  </td>
                  <td>
                    <Link to="/jobs/$jobId" params={{ jobId: r.job.id }} className="link">
                      {r.job.reference}
                    </Link>{' '}
                    {r.way}
                  </td>
                  <td>
                    {r.job.dockBooking ? (
                      (r.job.dockBooking.reference ?? 'Booked')
                    ) : required && mayBook ? (
                      <Button
                        variant="quiet"
                        className="small-btn border-accent-2! text-accent-2-700!"
                        onClick={() => setBooking(r.job)}
                      >
                        Book<span className="sr-only"> the dock for {r.job.reference}</span>
                      </Button>
                    ) : required ? (
                      <span className="font-semibold text-accent-2-700">Not booked</span>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

/** VEN-02: records the job's dock as booked, with the venue's reference if it gave one. */
function BookDock({ job, onDone }: { job: VenueJob; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [reference, setReference] = useState('');
  const book = useMutation({
    mutationFn: () => bookDock(job.id, reference.trim() || null),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['venues'] });
      await queryClient.invalidateQueries({ queryKey: docksToBookQuery.queryKey });
      onDone();
    },
  });
  return (
    <form
      aria-label="Dock booked"
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        book.mutate();
      }}
    >
      <Field
        label="The venue’s reference (optional)"
        value={reference}
        onChange={(e) => setReference(e.currentTarget.value)}
      />
      {book.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {book.error.message}
        </p>
      ) : null}
      <span className="flex gap-2">
        <Button type="submit" disabled={book.isPending}>
          Save
        </Button>
        <Button type="button" variant="quiet" onClick={onDone}>
          Cancel
        </Button>
      </span>
    </form>
  );
}

/** The notes the fields don't hold: dock, vehicles, parking, and anything else. */
function AccessNotes({ access: a }: { access: VenueAccessInput }) {
  const notes: Array<[string, string | null]> = [
    ['Dock', a.dockNotes],
    ['Vehicles', a.vehicleNotes],
    ['Parking', a.parkingNotes],
    ['Other', a.accessNotes],
  ];
  const said = notes.filter((n): n is [string, string] => !!n[1]);
  if (said.length === 0) return <p className="text-sm text-muted">No notes</p>;
  return (
    <dl className="dl text-sm">
      {said.map(([label, text]) => (
        <div key={label} className="contents">
          <dt>{label}</dt>
          <dd className="whitespace-pre-wrap">{text}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * VEN-08: the venue's dock photos, each opened from here. The app's content security policy admits
 * images from itself only, so a stored photo is not drawn inline.
 */
function DockPhotos({ venueId }: { venueId: string }) {
  const files = useQuery(venueFilesQuery(venueId));
  const photos = (files.data ?? []).filter((f) => f.kind === 'photo' && f.topic === 'dock');
  return (
    <section className="panel flex flex-col gap-2 p-4" aria-labelledby="access-photos">
      <div className="flex items-center">
        <h3 id="access-photos" className="panel-label flex-1">
          Photos
        </h3>
        <span className="text-xs text-muted">Dock</span>
      </div>
      {photos.length === 0 ? (
        <p className="text-sm text-muted">No dock photos. Upload them in Rooms.</p>
      ) : (
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {photos.map((p) => (
            <li key={p.id} className="photo-tile">
              <span className="text-xs">{p.caption ?? p.room?.name ?? 'Dock'}</span>
              <OpenButton fileId={p.file.id} label={p.caption ?? 'dock photo'} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** The view marks unset fields optional; the form and the summary work with every field said. */
function complete(a: Partial<VenueAccessInput>): VenueAccessInput {
  return {
    dockNotes: a.dockNotes ?? null,
    vehicleMaxLengthMm: a.vehicleMaxLengthMm ?? null,
    vehicleNotes: a.vehicleNotes ?? null,
    liftWidthMm: a.liftWidthMm ?? null,
    liftDepthMm: a.liftDepthMm ?? null,
    liftHeightMm: a.liftHeightMm ?? null,
    liftDoorWidthMm: a.liftDoorWidthMm ?? null,
    liftDoorHeightMm: a.liftDoorHeightMm ?? null,
    liftCapacityKg: a.liftCapacityKg ?? null,
    dockBookingRequired: a.dockBookingRequired ?? false,
    dockBookingLeadDays: a.dockBookingLeadDays ?? null,
    curfew: a.curfew ?? null,
    parkingNotes: a.parkingNotes ?? null,
    bondStore: a.bondStore ?? false,
    access24h: a.access24h ?? false,
    accessNotes: a.accessNotes ?? null,
    deliveryHours: a.deliveryHours ?? [],
  };
}

const asText = (n: number | null) => (n === null ? '' : String(n));
function wholeNumber(text: string): number | null | 'bad' {
  if (!text.trim()) return null;
  const n = Number(text);
  return Number.isInteger(n) && n > 0 ? n : 'bad';
}

function AccessForm({
  venueId,
  access,
  mode,
  zone,
  onDone,
  onChanged,
}: {
  venueId: string;
  access: VenueAccessInput;
  mode: AccessMode;
  zone: string;
  onDone: () => void;
  onChanged: () => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const fields = mode !== 'notes_only';
  const notes = mode !== 'fields_only';
  const [a, setA] = useState(access);
  const [measures, setMeasures] = useState({
    vehicleMaxLengthM:
      access.vehicleMaxLengthMm === null ? '' : String(access.vehicleMaxLengthMm / 1000),
    liftWidthMm: asText(access.liftWidthMm),
    liftDepthMm: asText(access.liftDepthMm),
    liftHeightMm: asText(access.liftHeightMm),
    liftDoorWidthMm: asText(access.liftDoorWidthMm),
    liftDoorHeightMm: asText(access.liftDoorHeightMm),
    liftCapacityKg: asText(access.liftCapacityKg),
    dockBookingLeadDays: asText(access.dockBookingLeadDays),
  });
  const [hours, setHours] = useState<Record<number, { opens: string; closes: string }>>(() =>
    Object.fromEntries(
      access.deliveryHours.map((h) => [h.weekday, { opens: h.opens, closes: h.closes }]),
    ),
  );
  const [problem, setProblem] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: (input: VenueAccessInput) => setAccess(venueId, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: accessQuery(venueId).queryKey });
      await onChanged();
      onDone();
    },
  });
  // The value is read now: React clears the event's target before a state updater runs.
  const note = (key: keyof VenueAccessInput) => (e: { currentTarget: { value: string } }) => {
    const value = e.currentTarget.value || null;
    setA((now) => ({ ...now, [key]: value }));
  };

  function submit(event: FormEvent) {
    event.preventDefault();
    const numbers: Record<string, number | null> = {};
    for (const [key, text] of Object.entries(measures)) {
      if (key === 'vehicleMaxLengthM') continue;
      const n = key === 'dockBookingLeadDays' && text.trim() === '0' ? 0 : wholeNumber(text);
      if (n === 'bad')
        return setProblem('Measures, weights and days are whole numbers above zero.');
      numbers[key] = n;
    }
    const metres = measures.vehicleMaxLengthM.trim();
    const vehicle = metres ? Math.round(Number(metres) * 1000) : null;
    if (metres && !(vehicle! > 0))
      return setProblem('Give the vehicle length in metres, like 12.5.');
    const deliveryHours: DeliveryHours[] = [];
    for (const day of DAYS) {
      const h = hours[day];
      if (!h || (!h.opens && !h.closes)) continue;
      if (!h.opens || !h.closes || h.closes <= h.opens) {
        return setProblem(`${WEEKDAYS[day]}: give both times, closing after opening.`);
      }
      deliveryHours.push({ weekday: day, opens: h.opens, closes: h.closes });
    }
    setProblem(undefined);
    save.mutate({
      ...a,
      vehicleMaxLengthMm: vehicle,
      liftWidthMm: numbers['liftWidthMm'] ?? null,
      liftDepthMm: numbers['liftDepthMm'] ?? null,
      liftHeightMm: numbers['liftHeightMm'] ?? null,
      liftDoorWidthMm: numbers['liftDoorWidthMm'] ?? null,
      liftDoorHeightMm: numbers['liftDoorHeightMm'] ?? null,
      liftCapacityKg: numbers['liftCapacityKg'] ?? null,
      dockBookingLeadDays: a.dockBookingRequired ? (numbers['dockBookingLeadDays'] ?? null) : null,
      deliveryHours,
    });
  }

  const measure = (key: keyof typeof measures, label: string) => (
    <Field
      label={label}
      inputMode="numeric"
      value={measures[key]}
      onChange={(e) => {
        const value = e.currentTarget.value;
        setMeasures((m) => ({ ...m, [key]: value }));
      }}
    />
  );

  return (
    <form onSubmit={submit} className="flex max-w-3xl flex-col gap-5">
      {fields ? (
        <>
          <fieldset className="flex flex-col gap-2">
            <legend className="text-xs text-ink-soft">Delivery hours ({zone})</legend>
            <p className="text-xs text-muted">Leave a day empty if nothing is known for it.</p>
            <div className="grid grid-cols-[7rem_1fr_1fr] items-center gap-2 text-sm">
              {DAYS.map((day) => (
                <div key={day} className="contents">
                  <span>{WEEKDAYS[day]}</span>
                  <input
                    type="time"
                    className="input"
                    aria-label={`${WEEKDAYS[day]} opens`}
                    value={hours[day]?.opens ?? ''}
                    onChange={(e) => {
                      const opens = e.currentTarget.value;
                      setHours((h) => ({ ...h, [day]: { opens, closes: h[day]?.closes ?? '' } }));
                    }}
                  />
                  <input
                    type="time"
                    className="input"
                    aria-label={`${WEEKDAYS[day]} closes`}
                    value={hours[day]?.closes ?? ''}
                    onChange={(e) => {
                      const closes = e.currentTarget.value;
                      setHours((h) => ({ ...h, [day]: { opens: h[day]?.opens ?? '', closes } }));
                    }}
                  />
                </div>
              ))}
            </div>
          </fieldset>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="access-curfew" className="text-xs text-ink-soft">
                Curfew ({zone})
              </label>
              <input
                id="access-curfew"
                type="time"
                className="input"
                value={a.curfew ?? ''}
                onChange={(e) => {
                  const curfew = e.currentTarget.value || null;
                  setA((now) => ({ ...now, curfew }));
                }}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={a.dockBookingRequired}
                  onChange={(e) => {
                    const on = e.currentTarget.checked;
                    setA((now) => ({ ...now, dockBookingRequired: on }));
                  }}
                />
                Dock booking required
              </label>
              {a.dockBookingRequired ? measure('dockBookingLeadDays', 'How many days ahead') : null}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {measure('vehicleMaxLengthM', 'Longest vehicle (m)')}
            {measure('liftCapacityKg', 'Goods lift takes (kg)')}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {measure('liftWidthMm', 'Lift width inside (mm)')}
            {measure('liftDepthMm', 'Lift depth inside (mm)')}
            {measure('liftHeightMm', 'Lift height inside (mm)')}
            {measure('liftDoorWidthMm', 'Lift door width (mm)')}
            {measure('liftDoorHeightMm', 'Lift door height (mm)')}
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={a.access24h}
                onChange={(e) => {
                  const on = e.currentTarget.checked;
                  setA((now) => ({ ...now, access24h: on }));
                }}
              />
              Open 24 hours
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={a.bondStore}
                onChange={(e) => {
                  const on = e.currentTarget.checked;
                  setA((now) => ({ ...now, bondStore: on }));
                }}
              />
              Bond store
            </label>
          </div>
        </>
      ) : null}
      {notes ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Dock" value={a.dockNotes ?? ''} onChange={note('dockNotes')} />
          <Field label="Vehicles" value={a.vehicleNotes ?? ''} onChange={note('vehicleNotes')} />
          <Field label="Parking" value={a.parkingNotes ?? ''} onChange={note('parkingNotes')} />
          <Field
            label="How to get in"
            hint="What the driver and crew need to know: sign-in, ramps, who opens up."
            value={a.accessNotes ?? ''}
            onChange={note('accessNotes')}
          />
        </div>
      ) : null}
      {problem || save.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {problem ?? save.error?.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          Save
        </Button>
        <Button type="button" variant="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/** VEN-02 at v1.971: a note per room, for a way in that differs from the venue's. */
function RoomNotes({ venueId, mayManage }: { venueId: string; mayManage: boolean }) {
  const rooms = useQuery(roomsQuery(venueId));
  if (!rooms.data || rooms.data.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <h4 className="panel-label mt-2">By room</h4>
      <ul className="flex flex-col gap-2 text-sm">
        {rooms.data.map((r) => (
          <RoomNote
            key={r.id}
            venueId={venueId}
            room={{ id: r.id, name: r.name, accessNotes: r.accessNotes ?? null }}
            mayManage={mayManage}
          />
        ))}
      </ul>
    </div>
  );
}

function RoomNote({
  venueId,
  room,
  mayManage,
}: {
  venueId: string;
  room: { id: string; name: string; accessNotes: string | null };
  mayManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(room.accessNotes ?? '');
  const save = useMutation({
    mutationFn: () => updateRoom(venueId, room.id, text.trim() || null),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: roomsQuery(venueId).queryKey });
      setEditing(false);
    },
  });
  if (editing) {
    return (
      <li className="flex flex-col gap-2">
        <Field
          label={`Getting to ${room.name}`}
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
        />
        {save.error ? (
          <p role="alert" className="font-semibold text-accent-2-700">
            {save.error.message}
          </p>
        ) : null}
        <span className="flex gap-2">
          <Button variant="quiet" disabled={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
          <Button variant="quiet" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </span>
      </li>
    );
  }
  return (
    <li className="flex flex-wrap items-baseline gap-2">
      <span className="font-semibold">{room.name}</span>
      <span className="text-ink-soft">{room.accessNotes ?? 'No note'}</span>
      {mayManage ? (
        <Button
          variant="quiet"
          className="small-btn"
          aria-label={`${room.accessNotes ? 'Edit the note' : 'Add a note'} for ${room.name}`}
          onClick={() => setEditing(true)}
        >
          Note <icons.edit size={14} aria-hidden />
        </Button>
      ) : null}
    </li>
  );
}
